
import React, { useEffect, useState, useCallback } from 'react';
import { analyticsAPI } from '../api';
import { useTranslation } from '../i18n';
import { toast } from '../components/common/Notifier';

export function AnalyticsPage() {
  const {t} = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [weeksOffset, setWeeksOffset] = useState(0);

  // 1. Получаем понятный текст рекомендации
  const getRecommendationText = () => {
    if (progression_recommendation.is_ready) {
      return t('analytics.recommendation_ready', {
        current: progression_recommendation.current_volume_km,
        suggested: progression_recommendation.suggested_volume_km,
        increment: progression_recommendation.increment_km,
      });
    }
    return t('analytics.recommendation_steady');
  };

  // 2. Получаем понятное описание ACWR
  const getAcwrDescription = (status) => {
    const map = {
      optimal: t('analytics.acwr_msg_optimal'),
      warning: t('analytics.acwr_msg_warning'),
      danger: t('analytics.acwr_msg_danger'),
      undertraining: t('analytics.acwr_msg_undertraining'),
      accumulating: t('analytics.acwr_msg_accumulating'),
    };
    return map[status] || '';
  };

  // 3. Получаем название проверки

  const getCheckLabel = (checkKey) => {
    const map = {
      cooldown: t('analytics.check_cooldown'),
      consistency: t('analytics.check_consistency'),
      hr_adaptation: t('analytics.check_hr_adaptation'),
      acwr_safe: t('analytics.check_acwr_safe'),
      phase_allowed: t('analytics.check_phase_allowed'),
    };
    return map[checkKey] || checkKey;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await analyticsAPI.getLoadSummary(weeksOffset);
      setData(response.data);
    } catch (e) {
      toast.error(t('error.general') || 'Не удалось загрузить аналитику');
    } finally {
      setLoading(false);
    }
  }, [t, weeksOffset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const goBackOneWeek = () => setWeeksOffset((o) => o + 1);
  const goForwardOneWeek = () => setWeeksOffset((o) => Math.max(0, o - 1));
  const goBackOneScreen = () => setWeeksOffset((o) => o + 8);
  const goForwardOneScreen = () => setWeeksOffset((o) => Math.max(0, o - 8));

  // Принятие рекомендации по объему
  const handleAcceptProgression = async () => {
    if (!data?.progression_recommendation?.suggested_volume_km) return;
    setAccepting(true);
    try {
      await analyticsAPI.acceptVolumeProgression({
        new_volume_km: data.progression_recommendation.suggested_volume_km,
      });
      toast.success(
          t('analytics.volume_updated_success') ||
          `Целевой объем успешно увеличен до ${data.progression_recommendation.suggested_volume_km} км!`
      );
      loadData();
    } catch (e) {
      toast.error(t('error.general'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
        <div className="max-w-6xl mx-auto py-12 text-center text-gray-400">
          <p className="animate-pulse">{t('common.loading') || 'Загрузка аналитики нагрузки...'}</p>
        </div>
    );
  }

  if (!data) return null;

  const {current_acwr, progression_recommendation, weeks_history} = data;

  // Хелпер стилей ACWR
  const getAcwrBadge = (status, value) => {
    if (value === null || status === 'accumulating') {
      return {
        bg: 'bg-gray-100 text-gray-700 border-gray-200',
        dot: 'bg-gray-400',
        label: t('analytics.acwr_accumulating') || 'Накопление данных (4 нед.)',
      };
    }
    switch (status) {
      case 'optimal':
        return {
          bg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
          dot: 'bg-emerald-500',
          label: `${value} · ${t('analytics.acwr_optimal') || 'Sweet Spot (Оптимально)'}`,
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 text-amber-800 border-amber-200',
          dot: 'bg-amber-500',
          label: `${value} · ${t('analytics.acwr_warning') || 'Внимание (Быстрый рост)'}`,
        };
      case 'danger':
        return {
          bg: 'bg-rose-50 text-rose-800 border-rose-200',
          dot: 'bg-rose-500',
          label: `${value} · ${t('analytics.acwr_danger') || 'Опасность (Перегруз)'}`,
        };
      case 'undertraining':
      default:
        return {
          bg: 'bg-blue-50 text-blue-800 border-blue-200',
          dot: 'bg-blue-500',
          label: `${value} · ${t('analytics.acwr_recovery') || 'Разгрузка / Снижение'}`,
        };
    }
  };

  const acwrBadge = getAcwrBadge(current_acwr.status, current_acwr.acwr);

  return (
      <div className="max-w-6xl mx-auto space-y-6 pb-12">

        {/* ── ХЕДЕР СТРАНИЦЫ ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              📊 {t('analytics.title') || 'Аналитика и Нагрузка (ACWR)'}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('analytics.subtitle') || 'Контроль перетренированности, баланс стресса и умная прогрессия объёма'}
            </p>
          </div>
          <button
              onClick={loadData}
              className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-700 transition-colors"
          >
            🔄 {t('common.refresh') || 'Обновить'}
          </button>
        </div>


        {/* ── 1. КАРТОЧКА-РЕКОМЕНДАЦИЯ РОСТА ОБЪЁМА (PROGRESSION ADVISOR) ── */}
        {progression_recommendation && (
            <div
                className={`rounded-2xl p-6 border transition-all shadow-md ${
                    progression_recommendation.is_ready
                        ? 'bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 text-white border-emerald-500/40 shadow-emerald-950/20'
                        : 'bg-white text-gray-800 border-gray-200'
                }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">

                {/* Левая колонка (Заголовок, описание и чек-лист) */}
                <div className="space-y-3 max-w-2xl">
                  <div className="flex items-center gap-2">
                <span className="text-xl">
                  {progression_recommendation.is_ready ? '🚀' : '🎯'}
                </span>
                    <h2 className="text-lg font-bold tracking-tight">
                      {progression_recommendation.is_ready
                          ? t('analytics.progression_ready_title') || 'Вы готовы к безопасному росту объёма!'
                          : t('analytics.progression_steady_title') || 'Текущая адаптация к объёму'}
                    </h2>
                  </div>

                  {/* Описание рекомендации */}
                  <p className={`text-sm ${progression_recommendation.is_ready ? 'text-teal-100' : 'text-gray-600'}`}>
                    {getRecommendationText()}
                  </p>

                  {/* Чек-лист проверок */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                    {Object.entries(progression_recommendation.checks || {}).map(([key, chk]) => (
                        <div
                            key={key}
                            className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border font-medium ${
                                progression_recommendation.is_ready
                                    ? chk.passed
                                        ? 'bg-emerald-800/40 border-emerald-500/30 text-emerald-200'
                                        : 'bg-rose-900/30 border-rose-500/30 text-rose-300'
                                    : chk.passed
                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                        : 'bg-gray-50 border-gray-200 text-gray-500'
                            }`}
                        >
                          <span>{chk.passed ? '✓' : '—'}</span>
                          <span>{getCheckLabel(key)}</span>
                        </div>
                    ))}
                  </div>
                </div>
                {/* ← Закрытие левой колонки */}

                {/* Правая колонка: Блок действия (Текущий объем -> Новый объем) */}
                {progression_recommendation.is_ready && (
                    <div
                        className="flex flex-col items-center lg:items-end justify-center gap-3 bg-white/10 backdrop-blur-md p-5 rounded-xl border border-white/20 shrink-0">
                      <div className="text-center lg:text-right">
                        <p className="text-xs uppercase tracking-wider text-teal-200 font-semibold">
                          {t('analytics.step_increase') || 'Предложение на следующий цикл'}:
                        </p>
                        <div className="flex items-baseline justify-center lg:justify-end gap-2 mt-1">
                    <span className="text-lg text-gray-300 line-through">
                      {progression_recommendation.current_volume_km}
                    </span>
                          <span className="text-3xl font-black text-amber-300">
                      {progression_recommendation.suggested_volume_km} {t('common.km')}
                    </span>
                          <span
                              className="text-xs font-bold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-500/40">
                      +{progression_recommendation.increment_km} {t('common.km')}
                    </span>
                        </div>
                      </div>

                      <button
                          type="button"
                          onClick={handleAcceptProgression}
                          disabled={accepting}
                          className="w-full py-2.5 px-6 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-gray-950 font-bold text-sm shadow-lg shadow-amber-950/30 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
                      >
                        {accepting ? '⏳...' : `✓ ${t('analytics.accept_volume') || 'Принять новый объём'}`}
                      </button>
                    </div>
                )}

              </div>
            </div>
        )}

        {/* ── 2. БЛОК KPI: ТЕКУЩИЙ ACWR И НАГРУЗКА ── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          {/* Карточка ACWR */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {t('analytics.acwr_ratio_title') || 'Баланс нагрузки (ACWR)'}
            </span>
              <span
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-bold border ${acwrBadge.bg}`}>
              <span className={`w-2 h-2 rounded-full ${acwrBadge.dot}`}/>
                {acwrBadge.label}
            </span>
            </div>

            <div className="flex items-baseline gap-3 pt-1">
            <span className="text-4xl font-black text-gray-900">
              {current_acwr.acwr !== null ? current_acwr.acwr : '—'}
            </span>
              <span className="text-xs text-gray-500 max-w-xs">
              {getAcwrDescription(current_acwr.status)}
            </span>
            </div>

            {/* Шкала зон ACWR */}
            <div className="pt-2">
              <div className="h-2 w-full bg-gray-100 rounded-full flex overflow-hidden">
                <div className="w-[30%] bg-blue-300" title="< 0.8: Разгрузка"/>
                <div className="w-[35%] bg-emerald-400" title="0.8 - 1.3: Sweet Spot"/>
                <div className="w-[15%] bg-amber-400" title="1.3 - 1.5: Внимание"/>
                <div className="w-[20%] bg-rose-400" title="> 1.5: Опасность"/>
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-mono">
                <span>0.0</span>
                <span className="text-emerald-700 font-bold">0.8 (Sweet spot) 1.3</span>
                <span className="text-amber-700">1.5</span>
                <span className="text-rose-700">2.0+</span>
              </div>
            </div>
          </div>

          {/* Острая нагрузка (7 дней) */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center justify-between">
              <span>{t('analytics.acute_load') || 'Острая нагрузка (7д)'}</span>
              <span>⚡</span>
            </p>
            <div className="flex items-baseline gap-1 pt-1">
              <span className="text-3xl font-black text-gray-900">{current_acwr.acute_load}</span>
              <span className="text-xs text-gray-400">pts</span>
            </div>
            <p className="text-xs text-gray-500">
              {t('analytics.acute_load_hint') || 'Суммарный стресс за неделю'}
            </p>
          </div>

          {/* Хроническая нагрузка (28 дней) */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center justify-between">
              <span>{t('analytics.chronic_load') || 'Базовая норма (28д)'}</span>
              <span>🏋️</span>
            </p>
            <div className="flex items-baseline gap-1 pt-1">
            <span className="text-3xl font-black text-gray-900">
              {current_acwr.chronic_load !== null ? current_acwr.chronic_load : '—'}
            </span>
              <span className="text-xs text-gray-400">pts</span>
            </div>
            <p className="text-xs text-gray-500">
              {t('analytics.chronic_load_hint') || 'Средний уровень за 4 недели'}
            </p>
          </div>

        </div>

        {/* ── 3. СВОДНАЯ ТАБЛИЦА ПОНЕДЕЛЬНОГО ТРЕНДА ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">


          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('analytics.history_title') || 'История нагрузки и качества по неделям'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('analytics.history_subtitle') || 'Динамика объёма, доли развивающих работ, sRPE-стресса и индекса выполнения'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={goBackOneScreen}
                disabled={!data?.has_more_history && weeksOffset === 0}
                className="w-9 h-9 flex
                items-center justify-center rounded-lg
                bg-gray-100 hover:bg-gray-200
                  text-lg font-bold text-gray-700
                  disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={t('analytics.prev_screen') || 'Предыдущие 8 недель'}
              >
                «
              </button>
              <button
                onClick={goBackOneWeek}
                disabled={loading}
                className="w-9 h-9 flex
                 items-center justify-center rounded-lg
                  bg-gray-100 hover:bg-gray-200 text-xl font-bold
                   text-gray-700 disabled:opacity-40 transition-colors"
                title={t('analytics.prev_week') || 'Предыдущая неделя'}
              >
                ‹
              </button>
              <button
                onClick={goForwardOneWeek}
                disabled={weeksOffset === 0}
                className="w-9 h-9 flex items-center justify-center
                 rounded-lg bg-gray-100 hover:bg-gray-200
                  text-xl font-bold text-gray-700 disabled:opacity-40
                   disabled:cursor-not-allowed transition-colors"
                title={t('analytics.next_week') || 'Следующая неделя'}
              >
                ›
              </button>
              <button
                onClick={goForwardOneScreen}
                disabled={weeksOffset === 0}
                className="w-9 h-9 flex items-center justify-center
                 rounded-lg bg-gray-100 hover:bg-gray-200 text-lg
                 font-bold text-gray-700 disabled:opacity-40
                 disabled:cursor-not-allowed transition-colors"
                title={t('analytics.next_screen') || 'Следующие 8 недель'}
              >
                »
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead
                  className="bg-gray-50/80 text-gray-400 font-semibold text-xs uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="py-3 px-4">{t('analytics.th_week') || 'Неделя'}</th>
                <th className="py-3 px-4">{t('analytics.th_volume') || 'Объём (Факт / План)'}</th>
                <th className="py-3 px-4">{t('analytics.th_quality') || 'Качество (% Quality)'}</th>
                <th className="py-3 px-4">{t('analytics.th_load') || 'Нагрузка (sRPE)'}</th>
                <th className="py-3 px-4">ACWR</th>
                <th className="py-3 px-4">{t('analytics.th_compliance') || 'Индекс плана'}</th>
                <th className="py-3 px-4 text-center">{t('analytics.th_downgrades') || 'Понижения'}</th>
              </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
              {weeks_history.map((wk) => {
                const acwrInfo = getAcwrBadge(wk.acwr_status, wk.acwr);

                return (
                    <tr
                        key={wk.week_start}
                        className={`transition-colors hover:bg-gray-50/70 ${
                            wk.is_current_week ? 'bg-blue-50/40 font-medium' : ''
                        }`}
                    >
                      {/* Неделя */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{wk.week_label}</span>
                          {wk.is_current_week && (
                              <span
                                  className="text-[10px] bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-xs">
                            {t('dashboard.current_week') || 'Текущая'}
                          </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                        {wk.completed_days} {t('analytics.days_completed') || 'дней выполнено'}
                      </span>
                      </td>

                      {/* Километраж */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-bold text-gray-900 text-base">{wk.actual_distance_km}</span>
                          <span className="text-xs text-gray-400">/ {wk.planned_distance_km} {t('common.km')}</span>
                        </div>
                        <div className="w-24 bg-gray-100 rounded-full h-1.5 mt-1 overflow-hidden">
                          <div
                              className={`h-full rounded-full ${
                                  wk.completion_pct >= 90 ? 'bg-emerald-500' : 'bg-blue-500'
                              }`}
                              style={{width: `${Math.min(wk.completion_pct, 100)}%`}}
                          />
                        </div>
                      </td>

                      {/* Доля качества */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="font-bold text-gray-800">{wk.quality_share_pct}%</span>
                        <span className="text-xs text-gray-400 ml-1.5">({wk.quality_distance_km} км)</span>
                      </td>

                      {/* Нагрузка sRPE */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="font-bold text-gray-900">{wk.total_load}</span>
                        <span className="text-xs text-gray-400 ml-1">pts ⚡</span>
                      </td>

                      {/* ACWR бейдж */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                      <span
                          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold border ${acwrInfo.bg}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${acwrInfo.dot}`}/>
                        {wk.acwr !== null ? wk.acwr : '—'}
                      </span>
                      </td>

                      {/* Compliance % */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {wk.avg_compliance_score !== null ? (
                            <div className="flex items-center gap-2">
                          <span
                              className={`font-extrabold text-sm ${
                                  wk.avg_compliance_score >= 85
                                      ? 'text-emerald-600'
                                      : wk.avg_compliance_score >= 70
                                          ? 'text-blue-600'
                                          : 'text-amber-600'
                              }`}
                          >
                            {wk.avg_compliance_score}%
                          </span>
                            </div>
                        ) : (
                            <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Понижения тренировок */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-center">
                        {wk.downgrades_count > 0 ? (
                            <span
                                className="text-xs font-bold text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                          {wk.downgrades_count} ⬇️
                        </span>
                        ) : (
                            <span className="text-xs text-gray-300">0</span>
                        )}
                      </td>

                    </tr>
                );
              })}
              </tbody>
            </table>
          </div>

        </div>

      </div>
  );
}